import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight, faXmark } from "@fortawesome/free-solid-svg-icons";

const ImageModal = ({
  images,
  selectedImage,
  setSelectedImage,
  showNavigation = true,
  hideArrowsOnMobile = false,
  immersive = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(
    images.findIndex((img) => img.src === selectedImage)
  );
  const modalRef = useRef(null);
  const touchStartX = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);

  const canNavigate = images.length > 1;
  const showArrows =
    showNavigation && canNavigate && !(hideArrowsOnMobile && isMobile);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => {
      const newIndex = prev === 0 ? images.length - 1 : prev - 1;
      setSelectedImage(images[newIndex].src);
      return newIndex;
    });
  }, [images, setSelectedImage]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => {
      const newIndex = prev === images.length - 1 ? 0 : prev + 1;
      setSelectedImage(images[newIndex].src);
      return newIndex;
    });
  }, [images, setSelectedImage]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setCurrentIndex(images.findIndex((img) => img.src === selectedImage));
  }, [selectedImage, images]);

  useEffect(() => {
    if (!immersive) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [immersive]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showArrows && e.key === "ArrowLeft") goToPrev();
      else if (showArrows && e.key === "ArrowRight") goToNext();
      else if (e.key === "Escape") setSelectedImage(null);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showArrows, setSelectedImage, goToNext, goToPrev]);

  const handleTouchStart = (e) => {
    if (!canNavigate) return;
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (!canNavigate || touchStartX.current === null) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;
    if (diff > 50) {
      goToNext();
    } else if (diff < -50) {
      goToPrev();
    }
    touchStartX.current = null;
  };

  const overlayClass = immersive
    ? "fixed inset-0 z-[10000] flex items-center justify-center bg-black/55 backdrop-blur-md cursor-pointer"
    : "fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 cursor-pointer";

  const content = (
    <div
      className={overlayClass}
      ref={modalRef}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onClick={() => setSelectedImage(null)}
      role="dialog"
      aria-modal="true"
    >
      {immersive && (
        <button
          type="button"
          className="absolute top-4 right-4 z-50 p-2.5 bg-black/80 hover:bg-black rounded-full transition-colors text-white"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedImage(null);
          }}
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faXmark} className="w-5 h-5" />
        </button>
      )}

      <div
        className={`relative flex items-center justify-center w-full h-full ${showArrows ? "px-14 sm:px-20" : "px-3 sm:px-4"}`}
        style={{ maxWidth: "96vw", maxHeight: "96vh" }}
      >
        {showArrows && (
          <>
            <button
              type="button"
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-50 p-2 sm:p-3 bg-black hover:bg-gray-800 rounded-full transition-colors text-white"
              onClick={(e) => {
                e.stopPropagation();
                goToPrev();
              }}
              aria-label="Previous image"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="w-5 h-5 sm:w-8 sm:h-8" />
            </button>
            <button
              type="button"
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-50 p-2 sm:p-3 bg-black hover:bg-gray-800 rounded-full transition-colors text-white"
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              aria-label="Next image"
            >
              <FontAwesomeIcon icon={faChevronRight} className="w-5 h-5 sm:w-8 sm:h-8" />
            </button>
          </>
        )}

        <div
          className="relative inline-block cursor-default"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={images[currentIndex]?.src ?? selectedImage}
            alt="Selected"
            className="rounded-lg max-w-[96vw] max-h-[92vh] w-auto object-contain mx-auto shadow-2xl"
          />
        </div>
      </div>
    </div>
  );

  if (immersive) {
    return createPortal(content, document.body);
  }

  return content;
};

export default ImageModal;
